// src/pages/Wallet/utils/tokenUtils.js
import { formatUnits } from 'ethers';

/**
 * Create a mapping of token addresses to balances
 * @param {string} nativeBalance - Native token balance in ether
 * @param {Array} tokens - Array of token objects
 * @returns {Object} - Map of token addresses to balances
 */
export const createTokenBalancesMap = (nativeBalance, tokens = []) => {
  const balancesMap = {};
  
  // Add native token balance
  balancesMap['native'] = nativeBalance || '0';
  
  // Add ERC-20 token balances
  tokens.forEach(tokenObj => {
    if (tokenObj.token && tokenObj.token.type === "ERC-20") {
      let rawVal = tokenObj.value;
      if (tokenObj.token.decimals) {
        try {
          rawVal = formatUnits(tokenObj.value, Number(tokenObj.token.decimals));
        } catch {
          rawVal = "0";
        }
      }
      balancesMap[tokenObj.token.address.toLowerCase()] = rawVal;
    }
  });
  
  return balancesMap;
};

/**
 * Calculate the total portfolio value in USD
 * @param {string} nativeBalance - Native token balance in ether
 * @param {number} nativePrice - Native token price in USD
 * @param {Array} tokens - Array of token objects
 * @param {Object} tokenPrices - Map of token addresses to prices
 * @returns {number} - Total portfolio value in USD
 */
export const calculatePortfolioValue = (nativeBalance, nativePrice, tokens = [], tokenPrices = {}) => {
  // Convert native balance to a number
  const numericBalance = nativeBalance ? parseFloat(nativeBalance) : 0;
  
  // Calculate native token USD value
  const nativeTokenValue = nativePrice && numericBalance ? numericBalance * nativePrice : 0;
  
  // Calculate total USD value of all ERC-20 tokens
  const tokensTotalValue = tokens.reduce((total, tObj) => {
    if (tObj.token && tObj.token.type === "ERC-20") {
      let rawVal = tObj.value;
      if (tObj.token.decimals) {
        try {
          rawVal = formatUnits(tObj.value, Number(tObj.token.decimals));
        } catch {
          rawVal = "0";
        }
      }
      const balNum = parseFloat(rawVal);
      const tokenPrice = tokenPrices[tObj.token.address?.toLowerCase()] || 0;
      return total + (balNum * tokenPrice);
    }
    return total;
  }, 0);
  
  // Add native token value and ERC-20 tokens value for total portfolio value
  return nativeTokenValue + tokensTotalValue;
};

/**
 * Calculate the 24-hour change percentage for the entire portfolio
 * Uses a weighted average based on each token's contribution to the portfolio value
 * 
 * @param {string} nativeBalance - Native token balance in ether
 * @param {number} nativePrice - Native token price in USD
 * @param {number} nativePriceChange - Native token 24h price change percentage
 * @param {Array} tokens - Array of token objects
 * @param {Object} tokenPrices - Map of token addresses to prices
 * @param {Object} tokenPriceChanges - Map of token addresses to 24h price change percentages
 * @returns {number} - Portfolio 24h change percentage
 */
export const calculatePortfolioChange = (
  nativeBalance, 
  nativePrice, 
  nativePriceChange = 0,
  tokens = [], 
  tokenPrices = {}, 
  tokenPriceChanges = {}
) => {
  // Calculate total portfolio value
  const portfolioValue = calculatePortfolioValue(nativeBalance, nativePrice, tokens, tokenPrices);
  
  // If portfolio value is 0, return 0% change
  if (portfolioValue <= 0) return 0;
  
  // Calculate native token value and weighted change
  const numericBalance = nativeBalance ? parseFloat(nativeBalance) : 0;
  const nativeTokenValue = nativePrice && numericBalance ? numericBalance * nativePrice : 0;
  
  // Calculate weighted change contribution from native token
  let weightedChange = (nativeTokenValue / portfolioValue) * (nativePriceChange || 0);
  
  // Calculate weighted changes from ERC-20 tokens
  tokens.forEach(tObj => {
    if (tObj.token && tObj.token.type === "ERC-20") {
      let rawVal = tObj.value;
      if (tObj.token.decimals) {
        try {
          rawVal = formatUnits(tObj.value, Number(tObj.token.decimals));
        } catch {
          rawVal = "0";
        }
      }
      
      const balNum = parseFloat(rawVal);
      const tokenAddress = tObj.token.address?.toLowerCase();
      const tokenPrice = tokenPrices[tokenAddress] || 0;
      const tokenValue = balNum * tokenPrice;
      
      if (tokenValue > 0) {
        const tokenChange = tokenPriceChanges[tokenAddress] || 0;
        const tokenWeight = tokenValue / portfolioValue;
        weightedChange += tokenWeight * tokenChange;
      }
    }
  });
  
  return weightedChange;
};

/**
 * Format the portfolio value for display
 * @param {number} value - The portfolio value in USD
 * @param {number} minDigits - Minimum fractional digits
 * @param {number} maxDigits - Maximum fractional digits
 * @returns {string} - Formatted value string
 */
export const formatPortfolioValue = (value, minDigits = 1, maxDigits = 2) => {
  return value > 0
    ? value.toLocaleString('en-US', { minimumFractionDigits: minDigits, maximumFractionDigits: maxDigits })
    : "0.0";
};

/**
 * Sort tokens by their USD value and name
 * @param {Array} tokens - Array of token objects
 * @param {Object} tokenPrices - Map of token addresses to prices
 * @returns {Array} - Sorted array of tokens
 */
export const sortTokensByValue = (tokens = [], tokenPrices = {}) => {
  const erc20List = tokens.filter(t => t.token && t.token.type === "ERC-20");
  
  return erc20List.slice().sort((a, b) => {
    const A = a.token, B = b.token;
    let rawA = a.value, rawB = b.value;
    
    if (A.decimals) {
      try { rawA = formatUnits(a.value, Number(A.decimals)); } catch {}
    }
    if (B.decimals) {
      try { rawB = formatUnits(b.value, Number(B.decimals)); } catch {}
    }
    
    const balA = parseFloat(rawA);
    const balB = parseFloat(rawB);
    const usdA = tokenPrices[A.address?.toLowerCase()] || 0;
    const usdB = tokenPrices[B.address?.toLowerCase()] || 0;
    const valA = balA * usdA;
    const valB = balB * usdB;
    
    if (valA > 0 && valB > 0) {
      return valB - valA;  // Sort by value descending
    } else if (valA > 0 && valB === 0) {
      return -1;  // Tokens with value come before those without
    } else if (valA === 0 && valB > 0) {
      return 1;
    } else {
      return A.name.localeCompare(B.name);  // Sort by name if no value
    }
  });
};

/**
 * Get a filtered and sorted token list
 * @param {Array} autoDetectedTokens - Tokens automatically detected
 * @param {Array} customTokens - User-added custom tokens
 * @param {Array} hiddenTokens - List of hidden token addresses
 * @returns {Array} - Combined, filtered, and sorted token list
 */
export const getFilteredTokenList = (autoDetectedTokens = [], customTokens = [], hiddenTokens = []) => {
  // Combine auto-detected and custom tokens
  const allTokens = [...autoDetectedTokens, ...customTokens];
  
  // Filter out hidden tokens
  return allTokens.filter(t => 
    !(t.token && t.token.address && hiddenTokens.includes(t.token.address.toLowerCase()))
  );
};